import type { SubscriptionAddonCode } from '~/subscription/addons/addons.types';
import {
  type BoundaryGroupDemographic, type BoundaryGroupDemographicServerModel,
  type DemographicIdCode,
} from '../boundaryGroups/boundaryGroups.repository';
import {
  type DemographicsPremiumListResponse,
  type DemographicsQueryBulkResultResponse, type DemographicsQueryResultResponse,
  type DemographicsRegion, type DemographicsRegionResponse, type DemographicsResult,
} from './demographics.repository';

export const createDemographicsRegionFromResponse = (region: DemographicsRegionResponse): DemographicsRegion => ({
  id: region.id,
  demographics: region.demographics.map(demographic => createDemographicFromResponse(demographic)),
});

export const createDemographicFromResponse = (
  response: BoundaryGroupDemographicServerModel
): BoundaryGroupDemographic => ({
  name: response.name,
  id: response.id,
  preData: response.pre_data,
  postData: response.post_data,
  percentage: response.percentage,
  category: response.category,
  isTrial: response.is_trial,
});

export const createServerModelFromDemographic = (
  demographic: BoundaryGroupDemographic
): BoundaryGroupDemographicServerModel => ({
  id: demographic.id,
  name: demographic.name,
  category: demographic.category,
  percentage: demographic.percentage,
  post_data: demographic.postData,
  pre_data: demographic.preData,
});

export const createDemographicsResultFromResponse = (
  response: DemographicsQueryResultResponse | DemographicsQueryBulkResultResponse
): DemographicsResult => {
  if (!('success' in response) || response.success) {
    return {
      success: true,
      name: response.name,
      value: response.value,
      agsValueOfTotal: response.ags_percentage,
      agsFormat: response.ags_format ?? undefined,
      functionUsed: response.function_used,
      category: response.category,
      percentage: response.percentage,
      demographicId: response.codename ?? response.demographics_id,
      preData: response.pre_data,
      postData: response.post_data,
    };
  }
  else {
    return {
      success: false,
      errorCode: response.error_code,
      demographicId: response.codename,
    };
  }
};

export const createDemographicsRegionsFromDemographicsPremiumListResponse = (
  response: DemographicsPremiumListResponse
): ReadonlyArray<DemographicsRegion> => (
  response.data.map(regionResponse => {
    const metricToPackages = new Map<DemographicIdCode, SubscriptionAddonCode[]>();
    for (const pkg of regionResponse.packages) {
      for (const metricId of pkg.metrics) {
        const existing = metricToPackages.get(metricId);
        if (existing) {
          existing.push(pkg.codename);
        }
        else {
          metricToPackages.set(metricId, [pkg.codename]);
        }
      }
    }

    return {
      id: regionResponse.id,
      demographics: regionResponse.demographics.map(demographicResponse => {
        const demographic = createDemographicFromResponse(demographicResponse);
        const packages = metricToPackages.get(demographic.id);

        return {
          ...demographic,
          package: packages,
        };
      }),
    };
  })
);
